// Boat service for making API calls
import { BACKEND_URL } from '../config';

class BoatService {
  // Get all boats in registry
  static async getBoatsRegistry(token) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/boats/registry`, {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      });
      
      // Log the response status to help with debugging
      console.log("Registry API response status:", response.status);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch boats registry');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching boats registry:', error);
      throw error;
    }
  }
  
  // Get boat by ID
  static async getBoatById(token, boatId) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/boats/registry/${boatId}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch boat');
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error fetching boat ${boatId}:`, error);
      throw error;
    }
  }
  
  // Register a new boat (admin only)
  static async registerBoat(token, boatData) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/boats/registry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify(boatData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to register boat');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error registering boat:', error);
      throw error;
    }
  }
  
  // Get available boats for tracking
  static async getAvailableBoats() {
    try {
      const response = await fetch(`${BACKEND_URL}/api/boats`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch available boats');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching available boats:', error);
      throw error;
    }
  }
  
  // Update boat (admin only)
  static async updateBoat(token, boatId, boatData) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/boats/registry/${boatId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify(boatData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update boat');
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error updating boat ${boatId}:`, error);
      throw error;
    }
  }
  
  // Delete boat (admin only)
  static async deleteBoat(token, boatId) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/boats/registry/${boatId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete boat');
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error deleting boat ${boatId}:`, error);
      throw error;
    }
  }
  
  // Assign boat to user (admin only)
  static async assignBoatToUser(token, boatId, userId) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/boats/assign/${boatId}/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to assign boat');
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error assigning boat ${boatId} to user ${userId}:`, error);
      throw error;
    }
  }
  
  // Unassign boat from user (admin only)
  static async unassignBoatFromUser(token, boatId, userId) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/boats/unassign/${boatId}/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to unassign boat');
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error unassigning boat ${boatId} from user ${userId}:`, error);
      throw error;
    }
  }
  
  // Get boat route/history
  static async getBoatRoute(boatId, hours = 24) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/boats/${boatId}/route?hours=${hours}`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch boat route');
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error fetching route for boat ${boatId}:`, error);
      throw error;
    }
  }
  
  // Get users with assigned boats (admin only)
  static async getUsersWithBoats(token) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/boats/users`, {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch users with boats');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching users with boats:', error);
      throw error;
    }
  }
}

export default BoatService; 